import { getBetStatus } from '@williamhill/rosi-ui-shared/src/lib/bet-status';
import { unitsValueMoney } from '@williamhill/rosi-ui-shared/src/lib/money';
import { BetStatusType, IBetLeg, ILegResult } from '@williamhill/rosi-ui-shared/src/types';
import { merge } from 'ramda';

/*
  There's no nice way yet to verify that the object has the expected structure as
    a specific type. Hence the data field TICKET_TYPE_VERSION.
  Adding non-optional, changing type of fields requires backward compatibility
*/
export const TICKET_TYPE_VERSION = '1.0.0';

export type AwsWafCaptcha = {
  renderCaptcha: (
    container: HTMLElement,
    config: {
      apiKey: string;
      onLoad: () => void;
      onSuccess: () => void;
      onPuzzleIncorrect: () => void;
      onError: (error: any) => void;
      skipTitle: boolean;
    }
  ) => void;
};
export type AwsWafIntegration = {
  fetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
};
export type AwsWafCookieDomainList = string[];

export type Leg = {
  id: string;
  event: { name: string };
  eventStartTime: string;
  market: { name: string };
  marketLine: number | null;
  price: PriceOdds | null;
  originalPrice?: PriceOdds;
  result: ILegResult | null;
  selection: { name: string };
};

export type Ticket = {
  cumulativeDecimalOdds: number;
  placedAt: string;
  potentialReturns: number;
  originalPotentialReturns?: number;
  retailTicketId: string;
  totalStake: number;
  legs: Leg[];
  betLegs?: BetLeg[];
  payout: number | null;
  typeName: string;
  status: string;
  paidOut: boolean;
  blockPayout: boolean;
  payoutExpiresAt: string | null;
  placedAtLocation: {
    venueId: string;
    deviceType: string;
    slotNumber: number;
  };
  estimatedOdds: PriceOdds | null;
  originalPrice: PriceOdds | null;
  price: PriceOdds | null;
  showVoidedAdjustmentMessage: boolean;
  parlayCardEditionId: string | null;
  rewardCreditsPerLine: number | null;
};

export type BetLeg = Pick<IBetLeg, 'legRefs' | 'type' | 'typeName'> & {
  price: PriceOdds;
  originalPrice?: PriceOdds | null;
};

export type PriceOdds = {
  a: string;
  f: string;
  d: number;
};

export type TicketView = Ticket & {
  TICKET_TYPE_VERSION: typeof TICKET_TYPE_VERSION;
  betStatus: BetStatusType;
  scannedAt: string;
};

export type Regions = 'us' | 'ca';

export const US_STATES = [
  'az',
  'co',
  'dc',
  'ia',
  'il',
  'in',
  'ky',
  'ks',
  'la',
  'ma',
  'md',
  'mi',
  'ms',
  'nc',
  'ne',
  'nj',
  'nv-wh',
  'nv',
  'oh',
  'pa',
  'pr',
  'va',
  'wa-ms',
  'wa-sk',
  'wv',
  'or',
  'nm',
  'me',
  'bs'
] as const;

export const CA_STATES = ['ca-on'] as const;

export type States = (typeof US_STATES)[number] | (typeof CA_STATES)[number];

export const UsStatesByCodes: Record<string, (typeof US_STATES)[number]> = {
  '91': 'az',
  '83': 'co',
  '82': 'dc',
  '98': 'ia',
  '97': 'il',
  '96': 'in',
  '66': 'ky',
  '67': 'ks',
  '89': 'la',
  '64': 'ma',
  '92': 'md',
  '63': 'mi',
  '93': 'ms',
  '86': 'nc',
  '59': 'ne',
  '95': 'nj',
  '84': 'nv-wh',
  '85': 'nv',
  '79': 'oh',
  '94': 'pa',
  '81': 'pr',
  '45': 'va',
  '88': 'wa-ms',
  '87': 'wa-sk',
  '99': 'wv',
  '53': 'or',
  '57': 'nm',
  '65': 'me',
  '42': 'bs'
};

export const CaStatesByCodes: Record<string, (typeof CA_STATES)[number]> = {
  '80': 'ca-on'
};

export const AllStatesByCodes = { ...UsStatesByCodes, ...CaStatesByCodes };

export const StateToRegion: Record<States, Regions> = {
  ...(Object.fromEntries(US_STATES.map(s => [s, 'us'])) as Record<(typeof US_STATES)[number], Regions>),
  ...(Object.fromEntries(CA_STATES.map(s => [s, 'ca'])) as Record<(typeof CA_STATES)[number], Regions>)
};

const williamhillStates: States[] = ['nv-wh'];

export function retailToGeneralState(state: States): string {
  if (state === 'ca-on') {
    return 'on';
  }
  if (state === 'nv-wh') {
    return 'nv';
  }
  return state;
}

export function enrichScannedTicket(ticket: Ticket, scannedAt: string): TicketView {
  return {
    ...ticket,
    TICKET_TYPE_VERSION: TICKET_TYPE_VERSION,
    betStatus: getBetStatus({ ...ticket, payout: unitsValueMoney(ticket.payout || 0) }),
    scannedAt
  };
}

export function updateTicket(newTicketData: Ticket, prevTicket: TicketView): TicketView {
  const { scannedAt } = prevTicket;
  return {
    ...newTicketData,
    TICKET_TYPE_VERSION: TICKET_TYPE_VERSION,
    betStatus: getBetStatus({ ...newTicketData, payout: unitsValueMoney(newTicketData.payout || 0) }),
    scannedAt
  };
}

export function getStateDetails(
  retailTicketId: string
): { region: string; stateCode: string; state: States; urlBranding: string } | undefined {
  const stateCode = retailTicketId.substring(1, 3);
  if (stateCode.length < 2 || (!UsStatesByCodes[stateCode] && !CaStatesByCodes[stateCode])) {
    return undefined;
  }

  const usState: States = UsStatesByCodes[stateCode];

  const { region, state } = usState
    ? { region: 'us', state: usState }
    : { region: 'ca', state: CaStatesByCodes[stateCode] };
  const urlBranding = getUrlBranding(state);

  return { region, stateCode, state, urlBranding };
}

export function getUrlBranding(state: States): string {
  return williamhillStates.includes(state) ? 'wh' : 'czr';
}

export function transformLegacyData(
  legacyTicket: Omit<TicketView, keyof ReturnType<typeof fieldDefaultValueGenerator>>
): TicketView {
  const ticket = { ...legacyTicket, TICKET_TYPE_VERSION: TICKET_TYPE_VERSION };
  return merge(fieldDefaultValueGenerator(), ticket) as TicketView;
}

function fieldDefaultValueGenerator() {
  return {
    scannedAt: getCurrentDateTime()
  };
}

export function getCurrentDateTime() {
  return new Date().toISOString();
}
